import compact from 'lodash/compact';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';

import { Group } from '@lumapps/groups/types';
import { NavigationMap, NavigationMapItem } from '@lumapps/navigation/types';
import createSlice, { PayloadAction } from '@lumapps/redux/createSlice';

export interface State {
    navigationMapItem: NavigationMapItem;
    visibleBy: Array<Pick<Group, 'id' | 'name' | 'uid'>>;
    errors: Record<string, boolean>;
    touched: Record<string, boolean>;
    nodeHasResource: boolean;
    feedAll?: Group;
}

const initialState: State = {
    navigationMapItem: {},
    visibleBy: [],
    errors: {},
    touched: {},
    nodeHasResource: false,
};

/**
 * Function to initialize the default state of the reducer.
 * Use this function to create the visibleBy array from the navigationMap visibleByGroupReference object.
 */
const initState = (state: State, visibleByGroupReferences: NavigationMap['visibleByGroupReferences']): State => {
    const { navigationMapItem, feedAll, nodeHasResource, ...restState } = state;

    /**
     * If there are already a visibility assigned to the current navigation item, we need to display their names
     * based on their ids.
     */
    const itemVisibleBy: Group[] =
        navigationMapItem.visibleBy && navigationMapItem.visibleBy.length > 0 && visibleByGroupReferences
            ? navigationMapItem.visibleBy.map((groupId) => ({
                  uid: groupId,
                  id: groupId,
                  // Using visibleByGroupReferences dict, get the names of the feeds
                  name: visibleByGroupReferences[groupId],
              }))
            : // If the map has no visibility set, set by default to ALL
              compact([feedAll]);

    /**
     * If node has a resource, the "Visibility" field changes wording to "Restrict navigation item visibility" field,
     * and we visually hide the ALL group from the field if it's the only one.
     */
    const filteredVisibleBy =
        nodeHasResource && itemVisibleBy.length === 1
            ? itemVisibleBy.filter((item) => ![feedAll?.id].includes(item.id))
            : itemVisibleBy;

    return {
        ...restState,
        navigationMapItem: {
            ...navigationMapItem,
            visibleBy: itemVisibleBy.map(({ id }) => id),
        },
        visibleBy: filteredVisibleBy,
        feedAll,
        nodeHasResource,
    };
};

export const reducers = {
    removeVisibleBy: (state: State, action: PayloadAction<Group>) => {
        const group = action.payload;
        const filteredVisibleBy = group ? state.visibleBy.filter((c) => c.id !== group.id) : [];

        set(state, 'visibleBy', filteredVisibleBy);

        /**
         * NavigationMapItem Visibility management.
         * - If array is empty and current Node has a resource, put back the ALL feed in the navigation's visibleBy
         * - Else set the navigation's visibility to filteredVisibleBy
         */
        const shouldPutBackFeedAll = state.nodeHasResource && filteredVisibleBy.length === 0;
        set(state, 'navigationMapItem', {
            ...state.navigationMapItem,
            visibleBy: shouldPutBackFeedAll ? compact([state.feedAll?.id]) : filteredVisibleBy.map((feed) => feed.id),
        });

        // If array is empty and current Node does NOT have a resource, display error message
        if (!state.nodeHasResource && filteredVisibleBy.length === 0) {
            set(state, 'errors', {
                ...state.errors,
                visibleBy: true,
            });
        }

        set(state, 'touched', {
            ...state.touched,
            visibleBy: true,
        });
    },
    addVisibleBy: (state: State, action: PayloadAction<Array<Pick<Group, 'uid' | 'id' | 'name'>>>) => {
        const visibleBy = uniqBy([...state.visibleBy, ...action.payload], 'id');
        const feedIds = visibleBy.map((feed) => feed.id);

        set(state, 'visibleBy', visibleBy);

        // If current Node has a resource, remove ALL Feed from the node when another feed is added
        set(state, 'navigationMapItem', {
            ...state.navigationMapItem,
            visibleBy: state.nodeHasResource ? feedIds.filter((feedId) => feedId !== state.feedAll?.id) : feedIds,
        });

        set(state, 'errors', {
            ...state.errors,
            visibleBy: false,
        });

        set(state, 'touched', {
            ...state.touched,
            visibleBy: true,
        });
    },
    updateNavigationMapItem: (state: State, action: PayloadAction<Partial<NavigationMapItem>>) => {
        set(state, 'navigationMapItem', {
            ...state.navigationMapItem,
            ...action.payload,
        });

        const touchedFields = Object.keys(action.payload || {}).reduce(
            (prev, curr) => ({ ...prev, [curr]: true }),
            state.touched,
        );

        set(state, 'touched', touchedFields);
    },
    setFieldError: (state: State, action: PayloadAction<{ field: string; hasError: boolean }>) => {
        const { field, hasError } = action.payload;

        set(state, 'errors', {
            ...state.errors,
            [field]: hasError,
        });
    },
    reset: (state: State) => {
        set(state, 'navigationMapItem', initialState.navigationMapItem);
        set(state, 'errors', initialState.errors);
        set(state, 'visibleBy', initialState.visibleBy);
        set(state, 'touched', initialState.touched);
        set(state, 'nodeHasResource', initialState.nodeHasResource);
        set(state, 'feedAll', initialState.feedAll);
    },
};

const slice = createSlice({
    domain: 'add-navigation-item',
    initialState,
    reducers,
});

const { actions, reducer } = slice;

export { actions, reducer, initialState, initState };
